import { useState, useEffect } from 'react';
import axios from 'axios';
import { FiSearch, FiUser, FiSlash, FiCheck } from 'react-icons/fi';

interface User {
  _id: string;
  email: string;
  username: string;
  role: 'USER' | 'ADMIN';
  isActive: boolean;
  currentLevel: number;
  totalStars: number;
  createdAt: string;
  lastLogin: string;
}

const UserList = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pagination.page, limit: pagination.limit, search }
      });
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/admin/users/${userId}`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const changeUserRole = async (userId: string, newRole: 'USER' | 'ADMIN') => {
    if (!confirm(`Are you sure you want to change this user's role to ${newRole}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/admin/users/${userId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
  };

  return (
    <div className="user-list">
      <h2>User Management</h2>

      <div className="user-list-header">
        <form onSubmit={handleSearch} className="search-form">
          <FiSearch />
          <input
            type="text"
            className="input"
            placeholder="Search by email or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Level</th>
                <th>Stars</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user._id}>
                  <td>
                    <div className="user-cell">
                      <FiUser />
                      <span>{user.username}</span>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <select
                      className="role-select"
                      value={user.role}
                      onChange={(e) => changeUserRole(user._id, e.target.value as 'USER' | 'ADMIN')}
                    >
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </select>
                  </td>
                  <td>{user.currentLevel}</td>
                  <td>{user.totalStars}</td>
                  <td>
                    <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                      {user.isActive ? 'Active' : 'Banned'}
                    </span>
                  </td>
                  <td>{new Date(user.lastLogin).toLocaleDateString()}</td>
                  <td>
                    <button
                      className={`btn-icon ${user.isActive ? 'danger' : 'success'}`}
                      onClick={() => toggleUserStatus(user._id, user.isActive)}
                      title={user.isActive ? 'Ban user' : 'Unban user'}
                    >
                      {user.isActive ? <FiSlash /> : <FiCheck />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-secondary"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <style>{`
        .user-list h2 {
          margin-bottom: var(--spacing-lg);
        }
        .user-list-header {
          margin-bottom: var(--spacing-lg);
        }
        .search-form {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          background: var(--bg-card);
          padding: var(--spacing-md);
          border-radius: var(--radius-md);
        }
        .search-form svg {
          color: var(--text-muted);
        }
        .search-form .input {
          flex: 1;
        }
        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card);
          border-radius: var(--radius-md);
          overflow: hidden;
          margin-bottom: var(--spacing-lg);
        }
        .data-table th,
        .data-table td {
          padding: var(--spacing-md);
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }
        .data-table th {
          background: var(--bg-tertiary);
          font-weight: 600;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }
        .user-cell {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }
        .role-select {
          padding: var(--spacing-xs) var(--spacing-sm);
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-family: inherit;
          cursor: pointer;
        }
        .status-badge {
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--radius-full);
          font-size: 0.75rem;
          font-weight: 600;
        }
        .status-badge.active {
          background: rgba(8, 217, 214, 0.2);
          color: var(--neon-green);
        }
        .status-badge.inactive {
          background: rgba(255, 46, 99, 0.2);
          color: var(--neon-pink);
        }
        .btn-icon {
          padding: var(--spacing-sm);
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: var(--radius-sm);
          transition: all var(--transition-fast);
        }
        .btn-icon:hover {
          background: var(--bg-tertiary);
        }
        .btn-icon.danger:hover {
          color: var(--neon-pink);
        }
        .btn-icon.success:hover {
          color: var(--neon-green);
        }
        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--spacing-lg);
        }
        .page-info {
          color: var(--text-secondary);
        }
        .loading {
          padding: var(--spacing-xl);
          text-align: center;
          color: var(--text-secondary);
        }
        @media (max-width: 1024px) {
          .data-table {
            font-size: 0.875rem;
          }
          .data-table th,
          .data-table td {
            padding: var(--spacing-sm);
          }
        }
      `}</style>
    </div>
  );
};

export default UserList;
